import React, { FunctionComponent } from 'react';
import { useRouteMatch } from 'react-router';
import { Button, Form, Popconfirm, Radio, Switch } from 'antd';
import { useStore } from 'effector-react';
import { RadioChangeEvent } from 'antd/lib/radio';
import { ExclamationCircleOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import routes from '@config/routes';
import componentContent from './MentionsOfficialAccountSelector.content.json';
import useTranslate from '@hooks/useTranslate';
import { goTo } from '@helpers/navigation';
import mentionsOfficialAccountUnread, { getUnread } from '@stores/mentionsOfficialAccountUnread';
import mentionsOfficialAccountPost, {
  checkAllPosts,
  refresh as refreshPosts,
} from '@stores/mentionsOfficialAccountPost';
import mentionsOfficialAccountComment, {
  checkAllComments,
  refresh as refreshComments,
} from '@stores/mentionsOfficialAccountComment';
import css from './MentionsOfficialAccountSelector.module.less';

const {
  mentionsOfficialAccountPost: mentionsOfficialAccountPostRoute,
  mentionsOfficialAccountComment: mentionsOfficialAccountCommentRoute,
} = routes;
const mentionsOfficialAccountRoutes = [
  mentionsOfficialAccountPostRoute,
  mentionsOfficialAccountCommentRoute,
];

interface MentionsOfficialAccountSelectorProps {}

const MentionsOfficialAccountSelector: FunctionComponent<
  MentionsOfficialAccountSelectorProps
> = () => {
  const match = useRouteMatch();
  const currentRoute = mentionsOfficialAccountRoutes.find((route) => route.path === match.path);
  const content = useTranslate(componentContent);
  const onChange = ({ target }: RadioChangeEvent) => goTo(target.value);
  const mentionsOfficialAccountUnreadStore = useStore(mentionsOfficialAccountUnread);
  const postStore = useStore(mentionsOfficialAccountPost);
  const commentStore = useStore(mentionsOfficialAccountComment);
  const isPostRoute = currentRoute?.name === 'mentionsOfficialAccountPost';
  const activeStore = isPostRoute ? postStore : commentStore;

  const displayChecked = (value: boolean) => {
    const filters = { ...activeStore.filters, needChecked: value };
    if (isPostRoute) {
      refreshPosts({ filters });
    } else {
      refreshComments({ filters });
    }
  };

  const checkAll = async () => {
    if (isPostRoute) {
      await checkAllPosts();
      refreshPosts({ filters: activeStore.filters });
    } else {
      await checkAllComments();
      refreshComments({ filters: activeStore.filters });
    }

    getUnread();
  };

  const getExclamationIcon = (routeName: string) => {
    const isIconDisplayed =
      mentionsOfficialAccountUnreadStore[
        routeName as 'mentionsOfficialAccountPost' | 'mentionsOfficialAccountComment'
      ];
    return (
      isIconDisplayed && (
        <div className={css.icon}>
          {currentRoute?.name === routeName ? (
            <ExclamationCircleFilled />
          ) : (
            <ExclamationCircleOutlined />
          )}
        </div>
      )
    );
  };

  return (
    <>
      <Radio.Group
        style={{ marginBottom: 16 }}
        onChange={onChange}
        value={currentRoute ? currentRoute.name : undefined}
      >
        {mentionsOfficialAccountRoutes.map((route) => (
          <Radio.Button
            key={route.name}
            value={route.name}
          >
            {getExclamationIcon(route.name)}
            {content[route.name]}
          </Radio.Button>
        ))}
      </Radio.Group>

      <Form layout='vertical'>
        <Form.Item label={content.displayChecked}>
          <Switch
            checked={activeStore.filters.needChecked}
            onChange={displayChecked}
            loading={!!activeStore.preloaders.getList}
          />
        </Form.Item>

        <Form.Item>
          <Popconfirm
            title={content.readAllConfirm}
            okText={content.readAll}
            cancelText={content.cancel}
            onConfirm={checkAll}
          >
            <Button
              loading={
                isPostRoute
                  ? !!postStore.preloaders.checkAllPosts
                  : !!commentStore.preloaders.checkAllComments
              }
              disabled={!!activeStore.preloaders.getList}
            >
              {content.readAll}
            </Button>
          </Popconfirm>
        </Form.Item>
      </Form>
    </>
  );
};

export default MentionsOfficialAccountSelector;
